// src/pages/TermsAndConditions.jsx
import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

const TermsAndConditions = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const backTo = location.state?.from || "/";

  const LAST_UPDATED = "January 12, 2026";
  const APP_NAME = "PIAS";

  return (
    <div className="min-h-screen bg-gray-100">
      <header className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 py-6 flex items-center justify-between">
          <div>
            <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">
              Terms and Conditions
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Last updated: {LAST_UPDATED}
            </p>
          </div>

          <button
            onClick={() => navigate(backTo)}
            className="text-sm font-medium text-green-600 hover:text-green-700"
          >
            Back
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl shadow-sm border p-6 sm:p-8">
          <p className="text-gray-700 leading-relaxed">
            By accessing and using <strong>{APP_NAME}</strong> (hereinafter,
            the “Application”), you agree to be bound by these{" "}
            <strong>Terms and Conditions of Use</strong>. If you do not agree
            with any of them, you must refrain from using the Application.
          </p>

          <Section title="1. Service description">
            <p className="text-gray-700 leading-relaxed">
              The Application provides features related to registered-user
              access, information management, and use of services associated
              with your account. Some features require creating an account,
              verifying your email address, and signing in.
            </p>
          </Section>

          <Section title="2. Registration and user account">
            <ul className="list-disc pl-5 text-gray-700 space-y-2">
              <li>
                Provide truthful, complete, and up-to-date information during
                registration.
              </li>
              <li>
                Maintain the confidentiality of your access credentials and
                control access to your devices.
              </li>
              <li>
                Immediately report any unauthorized use of your account.
              </li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              You are responsible for activity carried out through your account,
              except where it is attributable to security failures caused by
              the Application.
            </p>
          </Section>

          <Section title="3. Permitted use">
            <p className="text-gray-700 leading-relaxed">
              The user agrees to use the Application only for lawful purposes
              and in accordance with applicable law, public order, and these
              terms.
            </p>
            <p className="text-gray-700 leading-relaxed mt-4">
              The following are prohibited:
            </p>
            <ul className="list-disc pl-5 text-gray-700 space-y-2 mt-2">
              <li>Using the Application for fraudulent or unlawful purposes.</li>
              <li>
                Attempting to access systems, data, or features without
                authorization.
              </li>
              <li>
                Interfering or attempting to interfere with the normal
                operation of the Application (for example, attacks, abusive
                scraping, or unauthorized reverse engineering).
              </li>
            </ul>
          </Section>

          <Section title="4. Security and authentication">
            <p className="text-gray-700 leading-relaxed">
              To protect your account, the Application may use email
              verification mechanisms and security measures such as session
              tokens. The user agrees to keep credentials confidential and to
              sign out when appropriate, especially on shared devices.
            </p>
          </Section>

          <Section title="5. Technical cookies">
            <p className="text-gray-700 leading-relaxed">
              The Application uses <strong>strictly necessary technical cookies</strong>{" "}
              for authentication, session maintenance, and security. These
              cookies are not used for advertising or tracking purposes.
            </p>

            <div className="mt-6 overflow-x-auto">
              <table className="min-w-full border border-gray-200 rounded-lg overflow-hidden">
                <thead className="bg-gray-50">
                  <tr>
                    <Th>Cookie</Th>
                    <Th>Purpose</Th>
                    <Th>Approximate duration</Th>
                  </tr>
                </thead>
                <tbody className="bg-white">
                  <tr className="border-t">
                    <Td><code className="text-sm">pias_refresh</code></Td>
                    <Td>Authentication and session maintenance (refresh token in an HttpOnly cookie).</Td>
                    <Td>Up to 30 days (configurable)</Td>
                  </tr>
                  <tr className="border-t">
                    <Td><code className="text-sm">pias_csrf</code></Td>
                    <Td>Security: mitigation of CSRF attacks.</Td>
                    <Td>Session / configurable</Td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p className="text-gray-700 leading-relaxed mt-6">
              For more detail, see the{" "}
              <Link
                to="/cookies"
                state={{ from: backTo }}
                className="text-green-600 hover:text-green-700 font-medium"
              >
                Cookies Policy
              </Link>
              .
            </p>
          </Section>

          <Section title="6. Data protection">
            <p className="text-gray-700 leading-relaxed">
              The processing of personal data is described in the{" "}
              <Link
                to="/privacidad"
                state={{ from: backTo }}
                className="text-green-600 hover:text-green-700 font-medium"
              >
                Privacy Policy
              </Link>
              .
            </p>
          </Section>

          <Section title="7. Service availability">
            <p className="text-gray-700 leading-relaxed">
              The owner of the Application does not guarantee continuous and
              uninterrupted availability of the service. Interruptions may
              occur due to maintenance, updates, or technical incidents.
            </p>
          </Section>

          <Section title="8. Liability">
            <p className="text-gray-700 leading-relaxed">
              The Application is provided “as is”. To the extent permitted by
              law, the owner will not be liable for indirect or consequential
              damages arising from the use of, or inability to use, the
              Application.
            </p>
          </Section>

          <Section title="9. Modifications">
            <p className="text-gray-700 leading-relaxed">
              The owner reserves the right to modify these Terms and
              Conditions. Changes will take effect upon publication in the
              Application.
            </p>
          </Section>

          <Section title="10. Contact">
            <p className="text-gray-700 leading-relaxed">
              For questions about these Terms and Conditions, use the contact
              channels indicated in the Application.
            </p>
          </Section>

          <div className="mt-10 flex flex-col sm:flex-row gap-3">
            <Link
              to="/privacidad"
              state={{ from: "/terminos" }}
              className="inline-flex justify-center rounded-lg bg-white border border-gray-300 px-4 py-2 text-sm font-medium text-gray-800 hover:bg-gray-50"
            >
              Privacy Policy
            </Link>
            <Link
              to="/cookies"
              state={{ from: "/terminos" }}
              className="inline-flex justify-center rounded-lg bg-white border border-gray-300 px-4 py-2 text-sm font-medium text-gray-800 hover:bg-gray-50"
            >
              Cookies Policy
            </Link>
          </div>
        </div>

        <p className="text-xs text-gray-500 mt-6">
          Note: This is a general informational document. For specific cases,
          seek professional advice.
        </p>
      </main>
    </div>
  );
};

function Section({ title, children }) {
  return (
    <section className="mt-8">
      <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
      <div className="mt-3">{children}</div>
    </section>
  );
}

function Th({ children }) {
  return (
    <th className="text-left text-xs font-semibold text-gray-600 px-4 py-3 uppercase tracking-wider">
      {children}
    </th>
  );
}

function Td({ children }) {
  return <td className="text-sm text-gray-700 px-4 py-3 align-top">{children}</td>;
}

export default TermsAndConditions;
